import React, { memo, useCallback, useMemo, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Typography } from 'transfix-ui/core'
import { IBid, IError } from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { useDeclineCounterOfferMutation } from '../../graphql/DeclineCounterOfferMutation'

export interface IDeleteCounterOfferConfirmationModalProps
  extends Pick<IConfirmationModalProps, 'onClose'> {
  counterOfferId: IBid['id']
  onSuccess: () => Promise<void> | void
}

const DeleteCounterOfferConfirmationModal = memo<IDeleteCounterOfferConfirmationModalProps>(
  ({ counterOfferId, onClose, onSuccess }) => {
    const { trackEvent } = useMPAnalytics()
    const [mutationErrors, setMutationErrors] = useState<IError[]>([])
    const { openToast } = useGlobalToast()

    const [deleteCounterOffer, { error, loading }] = useDeclineCounterOfferMutation()

    const deleteHandler = useCallback(async () => {
      setMutationErrors([])
      const { data } = await deleteCounterOffer({
        variables: {
          input: {
            id: counterOfferId,
          },
        },
      })
      const { declineBid } = data || {}
      const { bid, errors = [] } = declineBid || {}
      const { id: bidId } = bid || {}

      if (errors.length) {
        trackEvent('Carrier Options', 'ERROR_DELETING_COUNTER_OFFER', {
          counterOfferId,
          errors,
        })
        setMutationErrors(errors)
      }

      if (bidId) {
        trackEvent('Carrier Options', 'DELETED_COUNTER_OFFER', {
          counterOfferId,
        })
        openToast({
          toastMessage: 'Successfully declined counter offer.',
          toastType: 'default',
        })
        onSuccess()
      }
    }, [counterOfferId, deleteCounterOffer, onSuccess, openToast, trackEvent])

    const actions: IConfirmationModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: loading,
          action: onClose,
        },
        {
          label: 'Delete',
          type: 'confirm',
          disabled: loading,
          action: deleteHandler,
        },
      ],
      [deleteHandler, loading, onClose]
    )

    return (
      <ConfirmationModal title='Delete the counter offer?' actions={actions} onClose={onClose} open>
        <ApolloErrorMessages mutationErrors={mutationErrors} apolloErrors={error} />
        <Typography variant='body2' component='div'>
          The carrier will no longer be able to accept the counter offer they received.
        </Typography>
      </ConfirmationModal>
    )
  }
)

export default DeleteCounterOfferConfirmationModal
